// @flow

import * as React from 'react';
import { Flex, Box } from 'react-system';
import Head from 'next/head';
import Button from '@material-ui/core/Button';
import { KeyboardArrowLeft } from '../icons/keyboard-arrow-left.js';
import { KeyboardArrowRight } from '../icons/keyboard-arrow-right.js';
import { useRouter } from './router';
import { Link } from './link';

const PageButton = props => (
  <Button
    {...props}
    css={{
      padding: 0,
      width: 32,
      height: 32,
      minWidth: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  />
);

const Page = (props: {|
  css?: any,
  className?: string,
  index: number,
  onPageChange: number => void,
  position?: 'prev' | 'current' | 'next' | null,
  children?: React.Node,
|}) => {
  const router = useRouter();
  const href = {
    pathname: router.pathname,
    query: {
      ...router.query,
      page: String(props.index),
    },
  };

  return (
    <Box css={props.css} className={props.className} p={1}>
      {(props.position === 'prev' || props.position === 'next') && (
        <Head>
          <link rel={props.position} href={router.getUrl(href).absolute} />
        </Head>
      )}
      <Link href={href}>
        {({ href: url }) => (
          <PageButton
            css={props.position === 'current' ? { pointerEvents: 'none' } : {}}
            variant={props.position === 'current' ? 'contained' : 'text'}
            color="primary"
            size="small"
            onClick={e => {
              if (!(e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                props.onPageChange(props.index);
              }
            }}
            component="a"
            rel="nofollow"
            href={url.split('?')[0]}
          >
            {props.children != null ? props.children : props.index + 1}
          </PageButton>
        )}
      </Link>
    </Box>
  );
};

type Props = {|
  page: number,
  count: number,
  onPageChange: number => void,
  type?: 'google' | 'airnbnb',
|};

export const FixedPagination = (props: Props): React.Node => {
  const [itemsAround, showStartEnd] =
    props.type == null || props.type === 'google' ? [3, false] : [1, true];

  const start = Math.max(
    props.page -
      itemsAround -
      Math.max(props.page + itemsAround - (props.count - 1), 0),
    0,
  );

  const end = Math.min(
    props.page + itemsAround - Math.min(props.page - itemsAround, 0),
    props.count - 1,
  );

  const getPagePosition = (page, index) =>
    ({
      [page]: 'current',
      [page - 1]: 'prev',
      [page + 1]: 'next',
    }[index] || null);

  return (
    <Flex justifyContent="center">
      {props.page > 0 && (
        <Page onPageChange={props.onPageChange} index={props.page - 1}>
          <KeyboardArrowLeft size={24} />
        </Page>
      )}
      {0 < start && showStartEnd && (
        <>
          <Page onPageChange={props.onPageChange} index={0} />
          {1 < start && <Box>...</Box>}
        </>
      )}

      {Array.from(Array(end - start + 1))
        .map((_, i) => i + start)
        .map(i => (
          <Page
            key={i}
            onPageChange={props.onPageChange}
            index={i}
            position={getPagePosition(props.page, i)}
          />
        ))}

      {props.count - 1 > end && showStartEnd && (
        <>
          {props.count - 2 > end && <Box>...</Box>}
          <Page onPageChange={props.onPageChange} index={props.count - 1} />
        </>
      )}

      {props.page < props.count - 1 && (
        <Page onPageChange={props.onPageChange} index={props.page + 1}>
          <KeyboardArrowRight size={24} />
        </Page>
      )}
    </Flex>
  );
};
